import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { AccountSummary, CampaignRow, IdentitySummary, MEDIA_FORMATS } from "../../../../../database/mediaOps/mediaOpsApi";
import { useMutation, useOpRead } from "./hooks";
import {
  Banner,
  CheckLabel,
  FieldLabel,
  FormGrid,
  Grow,
  Hint,
  ListRow,
  Mono,
  Panel,
  PanelHead,
  ResultNotice,
  Row,
  Select,
  Small,
  Table,
  TableScroll,
  TextArea,
  TextInput,
  usd,
} from "./ui";

// Campaigns: a brief, explicit target accounts and local posting times
// become deterministic daily slots. Gaps stay visible with their reason.
// Nothing here publishes. Generated slots still need review and scheduling.

type Slot = {
  slotId: string;
  accountId: string;
  localDate: string;
  localTime: string;
  timezone: string;
  status: string;
  format?: string | null;
  gapReason?: string | null;
  revisionId?: string | null;
  job?: { status: string; stage?: string } | null;
  approval?: { status: string } | null;
  deliveries?: Array<{ scheduleState: string }>;
};

const slotVariant = (status: string) =>
  status === "gap" ? "danger" : status === "scheduled" || status === "approved" ? "success" : status === "generating" ? "info" : status === "ready" ? "accent" : "neutral";

const lines = (s: string) =>
  s
    .split(/\n/)
    .map((x) => x.trim())
    .filter(Boolean);

export const CampaignsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    mediaOps.listCampaigns(communityId).then((r) => {
      if (r.status === "blocked") {
        setErr(r.errors.map((e) => e.message).join(" ") || "Could not load campaigns.");
        return;
      }
      setCampaigns(r.data?.campaigns || []);
      setErr("");
    });
  }, [communityId]);
  useEffect(load, [load]);

  return (
    <>
      <Panel>
        <PanelHead>
          Campaigns <Grow />
          <Button size="sm" variant="secondary" onClick={load}>
            Refresh
          </Button>
          <Button size="sm" onClick={() => setCreating((v) => !v)}>
            {creating ? "Close" : "New campaign"}
          </Button>
        </PanelHead>
        {err ? <Small>{err}</Small> : null}
        {campaigns.length === 0 && !err ? <Small>No campaigns yet.</Small> : null}
        {campaigns.map((c) => (
          <ListRow key={c.campaignId} $active={selected === c.campaignId}>
            <Mono>{c.campaignId}</Mono>
            <Small>
              {c.startDate} to {c.endDate}
            </Small>
            {c.coverage ? (
              <Small>
                {c.coverage.withRevision} of {c.coverage.slots} slots have content
              </Small>
            ) : null}
            <Small>v{c.version}</Small>
            <Grow />
            <Button size="sm" variant="secondary" onClick={() => setSelected(selected === c.campaignId ? "" : c.campaignId)}>
              {selected === c.campaignId ? "Close" : "Open"}
            </Button>
          </ListRow>
        ))}
      </Panel>
      {creating ? (
        <CreateCampaign
          communityId={communityId}
          onCreated={(id) => {
            setCreating(false);
            load();
            setSelected(id);
          }}
        />
      ) : null}
      {selected ? <SlotsGrid communityId={communityId} campaignId={selected} /> : null}
    </>
  );
};

const CreateCampaign: React.FC<{ communityId: string; onCreated: (campaignId: string) => void }> = ({ communityId, onCreated }) => {
  const accountsRead = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const identitiesRead = useOpRead(() => mediaOps.listIdentities(communityId), [communityId]);
  const accounts: AccountSummary[] = accountsRead.result?.data?.accounts || [];
  const identities: IdentitySummary[] = identitiesRead.result?.data?.identities || [];
  const [identityId, setIdentityId] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [times, setTimes] = useState<Record<string, string>>({});
  const [niche, setNiche] = useState("");
  const [audience, setAudience] = useState("");
  const [voice, setVoice] = useState("");
  const [goals, setGoals] = useState("");
  const [constraints, setConstraints] = useState("");
  const [startDate, setStartDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [days, setDays] = useState("7");
  const [formats, setFormats] = useState<string[]>(["slideshow"]);
  const [budget, setBudget] = useState("0");
  const [rerolls, setRerolls] = useState("3");
  const brief = useMutation<{ briefId?: string; version?: number }>();
  const camp = useMutation<{ campaignId?: string }>();
  const edit = () => {
    brief.edited();
    camp.edited();
  };
  const idAccounts = accounts.filter((a) => a.identityId === identityId);
  const ready = identityId && chosen.length > 0 && niche.trim() && audience.trim() && voice.trim() && formats.length > 0;

  const submit = async () => {
    const b = await brief.run((k) =>
      mediaOps.saveBrief(k, communityId, {
        identityId,
        accountIds: chosen,
        audience: audience.trim(),
        niche: niche.trim(),
        voice: voice.trim(),
        goals: lines(goals),
        constraints: lines(constraints),
      })
    );
    if (!b || b.status === "blocked" || !b.data?.briefId) return;
    const res = await camp.run((k) =>
      mediaOps.createCampaign(k, communityId, {
        briefId: b.data.briefId,
        briefVersion: b.data.version || 1,
        startDate,
        days: Number(days) || 7,
        formatMix: Object.fromEntries(formats.map((f) => [f, 1])),
        budget: { limitUsd: Number(budget) || 0, rerollAllowance: Number(rerolls) || 0 },
        targets: chosen.map((aid) => {
          const a = accounts.find((x) => x.id === aid);
          return {
            accountId: aid,
            timezone: a?.timezone || "UTC",
            localTimes: (times[aid] || "18:00")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          };
        }),
      })
    );
    if (res && res.status !== "blocked" && res.data?.campaignId) onCreated(res.data.campaignId);
  };

  return (
    <Panel>
      <PanelHead>New campaign</PanelHead>
      <Hint>A brief comes first. Then pick each account and its local posting times. Times use the account time zone.</Hint>
      <FormGrid>
        <FieldLabel>
          Identity
          <Select
            value={identityId}
            onChange={(e) => {
              setIdentityId(e.target.value);
              setChosen([]);
              edit();
            }}
          >
            <option value="">Choose an identity</option>
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.kind})
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Niche
          <TextInput value={niche} onChange={(e) => { setNiche(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Start date
          <TextInput type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Days
          <TextInput type="number" min={1} max={28} value={days} onChange={(e) => { setDays(e.target.value); edit(); }} />
        </FieldLabel>
      </FormGrid>
      {idAccounts.map((a) => (
        <ListRow key={a.id} $active={chosen.includes(a.id)}>
          <CheckLabel>
            <input
              type="checkbox"
              checked={chosen.includes(a.id)}
              onChange={() => {
                setChosen((prev) => (prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id]));
                edit();
              }}
            />
            @{a.handle} ({a.platform})
          </CheckLabel>
          {chosen.includes(a.id) ? (
            <FieldLabel style={{ flex: "1 1 220px" }}>
              Local times in {a.timezone}, comma separated
              <TextInput
                value={times[a.id] ?? "18:00"}
                onChange={(e) => {
                  const v = e.target.value;
                  setTimes((prev) => ({ ...prev, [a.id]: v }));
                  edit();
                }}
              />
            </FieldLabel>
          ) : null}
        </ListRow>
      ))}
      <FormGrid>
        <FieldLabel>
          Audience
          <TextArea value={audience} onChange={(e) => { setAudience(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Voice
          <TextArea value={voice} onChange={(e) => { setVoice(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Goals, one per line
          <TextArea value={goals} onChange={(e) => { setGoals(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Constraints, one per line
          <TextArea value={constraints} onChange={(e) => { setConstraints(e.target.value); edit(); }} />
        </FieldLabel>
      </FormGrid>
      <div>
        <Small>Formats</Small>
        <Row>
          {MEDIA_FORMATS.filter((f) => f !== "green_screen").map((f) => (
            <CheckLabel key={f}>
              <input
                type="checkbox"
                checked={formats.includes(f)}
                onChange={() => {
                  setFormats((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
                  edit();
                }}
              />
              {f}
            </CheckLabel>
          ))}
        </Row>
      </div>
      <FormGrid>
        <FieldLabel>
          Budget limit in USD
          <TextInput type="number" min={0} value={budget} onChange={(e) => { setBudget(e.target.value); edit(); }} />
        </FieldLabel>
        <FieldLabel>
          Reroll allowance
          <TextInput type="number" min={0} value={rerolls} onChange={(e) => { setRerolls(e.target.value); edit(); }} />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={submit} disabled={!ready} isLoading={brief.busy || camp.busy}>
          Save brief and create campaign
        </Button>
      </Row>
      <ResultNotice result={brief.result} okText="Brief saved." />
      <ResultNotice result={camp.result} okText="Campaign created. Slots are planned, nothing is generated or sent yet." />
    </Panel>
  );
};

const SlotsGrid: React.FC<{ communityId: string; campaignId: string }> = ({ communityId, campaignId }) => {
  const read = useOpRead(() => mediaOps.listSlots(communityId, campaignId), [communityId, campaignId]);
  const accountsRead = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const accounts: AccountSummary[] = accountsRead.result?.data?.accounts || [];
  const data = read.result?.data || {};
  const slots: Slot[] = data.slots || [];
  const [picked, setPicked] = useState<string[]>([]);
  const [budget, setBudget] = useState("0");
  const [rerollSlot, setRerollSlot] = useState<Slot | null>(null);
  const [feedback, setFeedback] = useState("");
  const gen = useMutation<{ slots?: Array<{ slotId: string; result: string; reason?: string; jobId?: string | null }> }>();
  const reroll = useMutation();

  const accountIds = useMemo(() => Array.from(new Set(slots.map((s) => s.accountId))), [slots]);
  const dates = useMemo(() => Array.from(new Set(slots.map((s) => s.localDate))).sort(), [slots]);
  const remaining = typeof data.rerollAllowance === "number" ? data.rerollAllowance - (data.rerollsUsed || 0) : null;

  const generate = async () => {
    const res = await gen.run((k) =>
      mediaOps.generateSlots(k, communityId, { campaignId, expectedVersion: data.version ?? 0, slotIds: picked, budgetLimitUsd: Number(budget) || 0 })
    );
    if (res && !res.transportError) {
      setPicked([]);
      read.reload();
    }
  };
  const doReroll = async () => {
    if (!rerollSlot?.revisionId) return;
    const res = await reroll.run((k) =>
      mediaOps.rerollSlot(k, communityId, {
        campaignId,
        slotId: rerollSlot.slotId,
        baseRevisionId: rerollSlot.revisionId,
        feedback: feedback.trim(),
        budgetLimitUsd: Number(budget) || 0,
      })
    );
    if (res && res.status !== "blocked") {
      setRerollSlot(null);
      setFeedback("");
      read.reload();
    }
  };

  if (read.result?.status === "blocked") return <ResultNotice result={read.result} />;
  const handle = (aid: string) => accounts.find((a) => a.id === aid)?.handle || aid;

  return (
    <Panel>
      <PanelHead>
        Slots for <Mono>{campaignId}</Mono> <Grow />
        <Button size="sm" variant="secondary" onClick={read.reload}>
          Refresh
        </Button>
      </PanelHead>
      <Row>
        <Small>Budget {usd(data.budget?.limitUsd)} limit, {usd(data.budget?.spentUsd)} spent.</Small>
        <Small>Rerolls left: {remaining === null ? "unknown" : remaining}</Small>
      </Row>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <th>Account</th>
              {dates.map((d) => (
                <th key={d}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accountIds.map((aid) => (
              <tr key={aid}>
                <td>@{handle(aid)}</td>
                {dates.map((d) => (
                  <td key={d}>
                    {slots
                      .filter((s) => s.accountId === aid && s.localDate === d)
                      .map((s) => (
                        <div key={s.slotId} style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 }}>
                          <CheckLabel>
                            <input
                              type="checkbox"
                              aria-label={`Select slot ${s.slotId}`}
                              checked={picked.includes(s.slotId)}
                              onChange={() => {
                                setPicked((prev) => (prev.includes(s.slotId) ? prev.filter((x) => x !== s.slotId) : [...prev, s.slotId]));
                                gen.edited();
                              }}
                            />
                            {s.localTime}
                          </CheckLabel>
                          <Tag size="sm" variant={slotVariant(s.status) as any}>
                            {s.status}
                          </Tag>
                          {s.gapReason ? <Small>Gap: {s.gapReason}</Small> : null}
                          {s.job ? <Small>Job {s.job.status}</Small> : null}
                          {s.revisionId ? (
                            <Button size="sm" variant="ghost" onClick={() => setRerollSlot(s)}>
                              Reroll
                            </Button>
                          ) : null}
                        </div>
                      ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
      {(data.gaps || []).length > 0 ? (
        <Banner $tone="warning">
          <strong>{data.gaps.length} slots have no content.</strong>
          {data.gaps.slice(0, 20).map((g: any) => (
            <Small key={g.slotId}>
              @{handle(g.accountId)} {g.localDate} {g.localTime}: {g.reason}
            </Small>
          ))}
        </Banner>
      ) : null}
      <FormGrid>
        <FieldLabel>
          Budget limit for this run in USD
          <TextInput type="number" min={0} value={budget} onChange={(e) => { setBudget(e.target.value); gen.edited(); reroll.edited(); }} />
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={generate} disabled={picked.length === 0} isLoading={gen.busy}>
          Generate {picked.length} selected
        </Button>
      </Row>
      <ResultNotice result={gen.result} />
      {(gen.result?.data?.slots || []).map((r) => (
        <Small key={r.slotId}>
          {r.slotId}: {r.result}
          {r.reason ? ` (${r.reason})` : ""}
        </Small>
      ))}
      {rerollSlot ? (
        <Panel>
          <PanelHead>Reroll {rerollSlot.localDate} {rerollSlot.localTime}</PanelHead>
          <Hint>Only this slot changes. It uses one reroll. Rerolls left: {remaining === null ? "unknown" : remaining}.</Hint>
          <FieldLabel>
            Feedback
            <TextArea value={feedback} onChange={(e) => { setFeedback(e.target.value); reroll.edited(); }} />
          </FieldLabel>
          <Row>
            <Button size="sm" onClick={doReroll} disabled={!feedback.trim() || remaining === 0} isLoading={reroll.busy}>
              Reroll this slot
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRerollSlot(null)}>
              Close
            </Button>
          </Row>
          <ResultNotice result={reroll.result} />
        </Panel>
      ) : null}
    </Panel>
  );
};

export default CampaignsView;
